import { ChangeDetectionStrategy, Component, computed, type ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroMicrophone } from '@ng-icons/heroicons/outline';
import { heroPauseSolid, heroPlaySolid } from '@ng-icons/heroicons/solid';
import { ToastrService, UiButton, UiIconButton } from '../../../../shared/ui';

/** Playback speeds the user can cycle through, mirroring WhatsApp's audio control. */
const PLAYBACK_RATES = [1, 1.5, 2] as const;
type PlaybackRate = (typeof PLAYBACK_RATES)[number];

/**
 * WhatsApp-style audio player: round play/pause, clickable progress bar, time label
 * and a 1×/1.5×/2× speed toggle. Wraps a native <audio> element under the hood — all
 * controls are driven via signals so the component remains OnPush-friendly.
 */
@Component({
  selector: 'app-audio-player',
  imports: [NgIcon, UiButton, UiIconButton],
  providers: [provideIcons({ heroPlaySolid, heroPauseSolid, heroMicrophone })],
  templateUrl: './audio-player.html',
  styleUrl: './audio-player.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'audio-player-host' },
})
export class AudioPlayerComponent {
  private readonly toast = inject(ToastrService);

  readonly src = input.required<string>();
  /** Slightly tighter footprint for inline contexts (composer preview). */
  readonly compact = input(false);

  private readonly audio = viewChild<ElementRef<HTMLAudioElement>>('audio');

  protected readonly isPlaying = signal(false);
  protected readonly currentTime = signal(0);
  protected readonly duration = signal(0);
  protected readonly playbackRate = signal<PlaybackRate>(1);
  protected readonly loaded = signal(false);

  protected readonly progressPct = computed(() => {
    const total = this.duration();
    if (!total) return 0;
    return Math.min(100, (this.currentTime() / total) * 100);
  });

  /** While loading we don't know the duration — show 0:00 so the bubble doesn't jump. */
  protected readonly displayTime = computed(() => {
    const total = this.duration();
    const current = this.currentTime();
    const remaining = total ? Math.max(0, total - current) : 0;
    return formatSeconds(this.isPlaying() ? current : remaining || total);
  });

  protected toggle(): void {
    const el = this.audio()?.nativeElement;
    if (!el) return;
    if (el.paused) {
      void el.play().catch(() => this.toast.error('Reprodução indisponível', 'Não foi possível tocar este áudio.'));
    } else {
      el.pause();
    }
  }

  protected cycleSpeed(): void {
    const next = nextPlaybackRate(this.playbackRate());
    this.playbackRate.set(next);
    const el = this.audio()?.nativeElement;
    if (el) el.playbackRate = next;
  }

  /** Click-to-seek on the progress bar. Pure geometry — no signal writes from the template. */
  protected seek(event: MouseEvent): void {
    const el = this.audio()?.nativeElement;
    const total = this.duration();
    if (!el || !total) return;
    const bar = event.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    el.currentTime = ratio * total;
    this.currentTime.set(el.currentTime);
  }

  protected onLoadedMetadata(): void {
    const el = this.audio()?.nativeElement;
    if (!el) return;
    // Some opus/ogg blobs report Infinity for duration until the first seek — workaround
    // by fast-forwarding to the end and back, which forces the browser to compute it.
    if (!Number.isFinite(el.duration)) {
      el.currentTime = 1e6;
      el.addEventListener('timeupdate', () => {
        el.currentTime = 0;
        this.duration.set(Number.isFinite(el.duration) ? el.duration : 0);
        this.loaded.set(true);
      }, { once: true });
      return;
    }
    this.duration.set(el.duration);
    this.loaded.set(true);
  }

  protected onTimeUpdate(): void {
    const el = this.audio()?.nativeElement;
    if (el) this.currentTime.set(el.currentTime);
  }

  protected onPlay(): void {
    this.isPlaying.set(true);
  }

  protected onPause(): void {
    this.isPlaying.set(false);
  }

  protected onEnded(): void {
    this.isPlaying.set(false);
    this.currentTime.set(0);
  }

  /** Keyboard scrubbing: ←/→ step 5s, Home/End jump to ends. */
  protected onProgressKeydown(event: KeyboardEvent): void {
    const el = this.audio()?.nativeElement;
    const total = this.duration();
    if (!el || !total) return;
    const step = 5;
    let next = el.currentTime;
    switch (event.key) {
      case 'ArrowRight':
        next = Math.min(total, el.currentTime + step);
        break;
      case 'ArrowLeft':
        next = Math.max(0, el.currentTime - step);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = total;
        break;
      default:
        return;
    }
    event.preventDefault();
    el.currentTime = next;
    this.currentTime.set(next);
  }
}

function nextPlaybackRate(current: PlaybackRate): PlaybackRate {
  const index = PLAYBACK_RATES.indexOf(current);
  return PLAYBACK_RATES[(index + 1) % PLAYBACK_RATES.length];
}

function formatSeconds(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '0:00';
  const total = Math.floor(value);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
